import { useMemo } from "react";
import { useStandardRole } from "@/hooks/useStandardRole";
import { useUserClinics } from "@/hooks/useUserClinics";
import {
  getPrivilegedPageAccess,
  type PrivilegedAccessRequirement,
} from "@/lib/privileged-page-access";

export type { PrivilegedAccessRequirement };

export const usePrivilegedPageAccess = () => {
  const { data: standardRole, isLoading: standardRoleLoading } = useStandardRole();
  const { data: userClinics = [], isLoading: clinicsLoading } = useUserClinics();

  const assignments = useMemo(() => {
    const byClinic = new Map(
      (userClinics ?? []).map((clinic) => [
        clinic.clinic_id,
        {
          legacyRole: clinic.role,
          accessMode: clinic.access_mode,
        },
      ])
    );

    const standardAssignments =
      standardRole?.assignments?.map((assignment) => {
        const legacy = byClinic.get(assignment.clinicId);
        return {
          standardRole: assignment.standardRole,
          legacyRole: assignment.legacyRole ?? legacy?.legacyRole ?? null,
          accessMode: assignment.accessMode ?? legacy?.accessMode ?? "full",
        };
      }) ?? [];

    const standardAssignmentClinicIds = new Set(standardRole?.assignments?.map((assignment) => assignment.clinicId) ?? []);
    const legacyOnlyAssignments = (userClinics ?? [])
      .filter((clinic) => !standardAssignmentClinicIds.has(clinic.clinic_id))
      .map((clinic) => ({
        legacyRole: clinic.role,
        accessMode: clinic.access_mode ?? "full",
      }));

    return [...standardAssignments, ...legacyOnlyAssignments];
  }, [standardRole?.assignments, userClinics]);

  const access = useMemo(
    () =>
      getPrivilegedPageAccess({
        isSuperAdmin: standardRole?.isSuperAdmin,
        assignments,
      }),
    [assignments, standardRole?.isSuperAdmin]
  );

  return {
    ...access,
    isLoading: standardRoleLoading || clinicsLoading,
  };
};
